import { useCallback, useEffect, useState } from "react";

export type PreviewItem = { url: string; name?: string };

export function useMediaLightbox() {
  const [imagePreview, setImagePreview] = useState<PreviewItem | null>(null);
  const [imagePreviewVisible, setImagePreviewVisible] = useState(false);
  const [imagePreviewList, setImagePreviewList] = useState<PreviewItem[]>([]);
  const [imagePreviewIndex, setImagePreviewIndex] = useState<number>(0);
  const [videoPreview, setVideoPreview] = useState<PreviewItem | null>(null);
  const [videoPreviewVisible, setVideoPreviewVisible] = useState(false);

  useEffect(() => {
    if (imagePreview) {
      const id = requestAnimationFrame(() => setImagePreviewVisible(true));
      return () => cancelAnimationFrame(id);
    } else {
      setImagePreviewVisible(false);
    }
  }, [imagePreview]);

  useEffect(() => {
    if (videoPreview) {
      const id = requestAnimationFrame(() => setVideoPreviewVisible(true));
      return () => cancelAnimationFrame(id);
    } else {
      setVideoPreviewVisible(false);
    }
  }, [videoPreview]);

  const closeImagePreview = useCallback(() => {
    setImagePreviewVisible(false);
    setTimeout(() => setImagePreview(null), 180);
  }, []);

  const closeVideoPreview = useCallback(() => {
    setVideoPreviewVisible(false);
    setTimeout(() => setVideoPreview(null), 180);
  }, []);

  useEffect(() => {
    if (!imagePreview) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        if (imagePreviewList.length > 0) {
          const next = (imagePreviewIndex + 1) % imagePreviewList.length;
          setImagePreviewIndex(next);
          setImagePreview(imagePreviewList[next] || null);
        }
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (imagePreviewList.length > 0) {
          const prev = (imagePreviewIndex - 1 + imagePreviewList.length) % imagePreviewList.length;
          setImagePreviewIndex(prev);
          setImagePreview(imagePreviewList[prev] || null);
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        closeImagePreview();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [closeImagePreview, imagePreview, imagePreviewIndex, imagePreviewList]);

  useEffect(() => {
    if (!imagePreview) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") closeImagePreview();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeImagePreview, imagePreview]);

  useEffect(() => {
    if (!videoPreview) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") closeVideoPreview();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeVideoPreview, videoPreview]);

  return {
    imagePreview,
    setImagePreview,
    imagePreviewVisible,
    imagePreviewList,
    setImagePreviewList,
    imagePreviewIndex,
    setImagePreviewIndex,
    closeImagePreview,
    videoPreview,
    setVideoPreview,
    videoPreviewVisible,
    closeVideoPreview,
  };
}
